import { useEffect, useId, useRef, useState } from 'react';
import { Info } from 'lucide-react';

interface WeightInfoTooltipProps {
  label: string;
  text: string;
  onInfoClick?: (label: string) => void;
}

export function WeightInfoTooltip({ label, text, onInfoClick }: WeightInfoTooltipProps) {
  const [open, setOpen] = useState(false);
  const tooltipId = useId();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div
      ref={containerRef}
      className="relative inline-flex items-center justify-center gap-1 text-[11px] leading-[1.3em] tracking-[0.09em] text-[#666] max-[359px]:text-xs max-[359px]:leading-[1.5em] max-[359px]:tracking-[0.02em]"
    >
      <span>{label}</span>
      <button
        type="button"
        className="inline-flex cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-[#999]"
        aria-label={`More information about ${label}`}
        aria-expanded={open}
        aria-controls={tooltipId}
        onClick={() => {
          setOpen((prev) => !prev);
          onInfoClick?.(label);
        }}
      >
        <Info size={16} strokeWidth={1.75} aria-hidden />
      </button>
      {open && (
        <div
          id={tooltipId}
          role="tooltip"
          className="absolute bottom-[calc(100%+10px)] left-1/2 z-[11] w-[120px] -translate-x-1/2 bg-white px-[15px] py-[15px] text-center text-xs leading-[15px] text-[#555] shadow-[0_0_6px_1px_rgb(0_0_0/10%)] before:absolute before:bottom-[-7px] before:left-1/2 before:block before:h-3.5 before:w-3.5 before:-translate-x-1/2 before:rotate-45 before:bg-white before:shadow-[2px_2px_4px_rgb(0_0_0/6%)] before:content-['']"
        >
          {text}
        </div>
      )}
    </div>
  );
}
